import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, Award, BookOpen, CheckCircle, Clock, ChevronRight, 
  Shield, AlertTriangle, Play, Loader2, ArrowRight, XCircle,
  RefreshCw, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const TrainingModule = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [showAssessment, setShowAssessment] = useState(false);
  const [assessmentData, setAssessmentData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    fetchModule();
  }, [moduleId]);

  const fetchModule = async () => {
    try {
      const res = await api.get(`/training/modules/${moduleId}`);
      setData(res.data);
      
      // Find first incomplete lesson or start from beginning
      const lessons = res.data.lessons || [];
      const firstIncomplete = lessons.findIndex(l => !l.is_completed);
      setCurrentLessonIndex(firstIncomplete >= 0 ? firstIncomplete : 0);
    } catch (err) {
      toast.error('Failed to load module');
      navigate('/talent/training');
    } finally {
      setLoading(false);
    }
  };

  const startModule = async () => {
    try {
      await api.post(`/training/modules/${moduleId}/start`);
      fetchModule();
    } catch (err) {
      toast.error('Failed to start module');
    }
  };

  const completeLesson = async (lessonId) => {
    try {
      const res = await api.post(`/training/modules/${moduleId}/lessons/${lessonId}/complete`);
      toast.success('Lesson completed!');
      
      if (res.data.all_lessons_complete) {
        toast.success('All lessons complete! You can now take the assessment.');
      }
      
      fetchModule();
      
      // Move to next lesson
      if (currentLessonIndex < (data?.lessons?.length || 0) - 1) {
        setCurrentLessonIndex(prev => prev + 1);
      }
    } catch (err) {
      toast.error('Failed to complete lesson');
    }
  };

  const loadAssessment = async () => {
    try {
      const res = await api.get(`/training/modules/${moduleId}/assessment`);
      setAssessmentData(res.data);
      setShowAssessment(true);
      setStartTime(Date.now());
      setAnswers({});
      setResults(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load assessment');
    }
  };

  const submitAssessment = async () => {
    if (!assessmentData) return;
    
    // Validate all questions answered
    if (Object.keys(answers).length < assessmentData.questions.length) {
      toast.error('Please answer all questions');
      return;
    }
    
    setSubmitting(true);
    try {
      const timeTaken = Math.round((Date.now() - startTime) / 1000);
      const res = await api.post(`/training/modules/${moduleId}/assessment/submit`, {
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          question_id: questionId,
          answer: parseInt(answer)
        })),
        time_taken_seconds: timeTaken
      });
      
      setResults(res.data);
      
      if (res.data.certification_awarded) {
        toast.success('Congratulations! You earned a certification!');
      }
    } catch (err) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const { module, lessons, progress, certification, assessment } = data || {};
  const currentLesson = lessons?.[currentLessonIndex];
  const allLessonsComplete = progress?.lessons_completed === lessons?.length;
  const isCertified = certification?.earned;

  // Assessment Results View
  if (results) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="assessment-results">
        <div className={`${results.passed ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-orange-500'} text-white`}>
          <div className="max-w-3xl mx-auto px-4 py-12 text-center">
            {results.passed ? (
              <>
                <Trophy className="w-16 h-16 mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-2">
                  {results.certification_awarded ? 'Certification Earned!' : 'You Passed!'}
                </h1>
                <p className="text-white/90">Score: {results.score}%</p>
                {results.badge_name && (
                  <Badge className="mt-4 bg-white text-green-700 text-lg px-4 py-2">
                    <Award className="w-5 h-5 mr-2" />
                    {results.badge_name}
                  </Badge>
                )}
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-2">Not Quite There</h1>
                <p className="text-white/90">
                  Score: {results.score}% (Need {results.pass_score_required}% to pass)
                </p>
              </>
            )}
          </div>
        </div>
        
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Card>
            <CardHeader>
              <CardTitle>Results Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {results.results.map((r, i) => (
                  <div key={i} className={`p-4 rounded-lg border ${r.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-start gap-3">
                      {r.is_correct ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium">Question {i + 1}</p>
                        {!r.is_correct && r.explanation && (
                          <p className="text-sm text-gray-600 mt-1">{r.explanation}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-4 mt-6">
                {!results.passed && (
                  <Button onClick={() => { setResults(null); loadAssessment(); }} className="flex-1">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retake Assessment
                  </Button>
                )}
                <Button 
                  variant={results.passed ? 'default' : 'outline'} 
                  onClick={() => navigate('/talent/training')}
                  className="flex-1"
                >
                  Back to Training Hub
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Assessment View
  if (showAssessment && assessmentData) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="assessment-view">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <div className="max-w-3xl mx-auto px-4 py-6">
            <h1 className="text-xl font-bold">{assessmentData.module_name} - Assessment</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-white/80">
              <span>{assessmentData.questions.length} questions</span>
              <span>Pass: {assessmentData.pass_score}%</span>
              {assessmentData.is_safety_critical && (
                <Badge variant="destructive">100% Required</Badge>
              )}
            </div>
          </div>
        </div>
        
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="space-y-6">
            {assessmentData.questions.map((q, qIndex) => (
              <Card key={q.id} data-testid={`question-${qIndex}`}>
                <CardContent className="p-6">
                  <p className="font-medium mb-4">
                    {qIndex + 1}. {q.question}
                  </p>
                  <RadioGroup
                    value={answers[q.id]?.toString()}
                    onValueChange={(value) => setAnswers(prev => ({ ...prev, [q.id]: value }))}
                  >
                    {q.options.map((option, oIndex) => (
                      <div key={oIndex} className="flex items-center space-x-3 py-2">
                        <RadioGroupItem value={oIndex.toString()} id={`q${q.id}-o${oIndex}`} />
                        <Label htmlFor={`q${q.id}-o${oIndex}`} className="cursor-pointer">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="flex gap-4 mt-8">
            <Button variant="outline" onClick={() => setShowAssessment(false)} className="flex-1">
              Back to Lessons
            </Button>
            <Button 
              onClick={submitAssessment} 
              disabled={submitting || Object.keys(answers).length < assessmentData.questions.length}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              data-testid="submit-assessment-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <>Submit Assessment</>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Module Learning View
  return (
    <div className="min-h-screen bg-slate-50" data-testid="training-module-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(`/talent/training/category/${module?.category_slug}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">{module?.name}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-white/80">
            <span className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              {lessons?.length} lessons
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {module?.estimated_minutes} min
            </span>
            {module?.is_safety_critical && (
              <Badge variant="destructive">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Safety Critical
              </Badge>
            )}
          </div>
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span>Progress</span>
              <span>{progress?.progress_percent || 0}%</span>
            </div>
            <Progress value={progress?.progress_percent || 0} className="h-2 bg-white/30" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lesson Navigation */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Lessons</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {lessons?.map((lesson, index) => (
                  <button
                    key={lesson.id}
                    onClick={() => setCurrentLessonIndex(index)}
                    className={`w-full text-left p-3 rounded-lg flex items-center gap-3 transition-colors ${
                      index === currentLessonIndex 
                        ? 'bg-purple-100 text-purple-700' 
                        : lesson.is_completed 
                          ? 'bg-green-50 text-green-700'
                          : 'hover:bg-gray-100'
                    }`}
                  >
                    {lesson.is_completed ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${
                        index === currentLessonIndex ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                      }`} />
                    )}
                    <span className="text-sm truncate">{lesson.title}</span>
                  </button>
                ))}
                
                {/* Assessment Button */}
                <button
                  onClick={allLessonsComplete ? loadAssessment : undefined}
                  disabled={!allLessonsComplete}
                  className={`w-full text-left p-3 rounded-lg flex items-center gap-3 mt-4 ${
                    allLessonsComplete 
                      ? 'bg-purple-600 text-white hover:bg-purple-700' 
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  data-testid="take-assessment-btn"
                >
                  <Award className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {isCertified ? 'Retake Assessment' : 'Take Assessment'}
                  </span>
                </button>
              </CardContent>
            </Card>
            
            {/* Certification Status */}
            {isCertified && (
              <Card className="mt-4 bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <Award className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="font-semibold text-green-700">{certification.badge_name}</p>
                  <p className="text-sm text-green-600">Score: {certification.score}%</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Lesson Content */}
          <div className="lg:col-span-2">
            {currentLesson ? (
              <Card>
                <CardHeader>
                  <CardTitle>{currentLesson.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown>{currentLesson.content}</ReactMarkdown>
                  </div>
                  
                  {currentLesson.video_url && (
                    <div className="mt-6 bg-gray-100 rounded-lg p-4">
                      <p className="text-sm text-gray-600">Video resource available</p>
                      <a 
                        href={currentLesson.video_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:underline text-sm"
                      >
                        Watch Video
                      </a>
                    </div>
                  )}
                  
                  <div className="flex justify-between mt-8 pt-4 border-t">
                    <Button
                      variant="outline"
                      disabled={currentLessonIndex === 0}
                      onClick={() => setCurrentLessonIndex(prev => prev - 1)}
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Previous
                    </Button>
                    
                    {currentLesson.is_completed ? (
                      <Button
                        disabled={currentLessonIndex === lessons.length - 1}
                        onClick={() => setCurrentLessonIndex(prev => prev + 1)}
                      >
                        Next
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        onClick={() => completeLesson(currentLesson.id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="complete-lesson-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark Complete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Select a lesson to begin</p>
                  {progress?.status === 'not_started' && (
                    <Button onClick={startModule} className="mt-4">
                      <Play className="w-4 h-4 mr-2" />
                      Start Training
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingModule;
